import { AxiosError } from "axios";
import { HttpStatus } from "constant";
import { getAccountInstance } from "generated/factory";
import { Login, RespondAuth } from "generated/models";
import AxiosInstance from "service/axiosInstance";
import {
    createErrorResponseStandard,
    createResponseStandard,
} from "utils/functions";
import {
    AuthMFAResponse,
    AuthResponse,
    IAuthenticationStrategy,
    MFASubmissionParams,
    TokenResponse,
} from "../authenticationTypes";

export class LocalAuthStrategy implements IAuthenticationStrategy {
    private readonly accountClient = getAccountInstance(AxiosInstance);

    async login({ email, password }: Login): Promise<AuthResponse> {
        try {
            const response = await this.accountClient.login({
                email,
                password,
            });

            if (response.status === HttpStatus.OK) {
                const loginData = response.data as
                    | TokenResponse
                    | AuthMFAResponse;

                // Check if MFA is required
                if (loginData && "ChallengeName" in loginData) {
                    return {
                        status: HttpStatus.OK,
                        data: {
                            ChallengeName: loginData.ChallengeName,
                            Session: loginData.Session,
                            ChallengeParameters: loginData.ChallengeParameters,
                        } as AuthMFAResponse,
                    };
                }

                // Convert to unified token response
                return {
                    status: HttpStatus.OK,
                    data: {
                        AccessToken: loginData.AccessToken || "",
                        IdToken: loginData.IdToken || "",
                        RefreshToken: loginData.RefreshToken,
                        LastLogin: loginData.LastLogin,
                    } as TokenResponse,
                };
            }

            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    }

    async submitMFACode({
        session,
        mfaCode,
        challengeName = "SMS_MFA",
    }: MFASubmissionParams): Promise<AuthResponse> {
        try {
            // Map our clean interface to the actual API interface
            const apiBody: RespondAuth = {
                session:
                    typeof session === "string"
                        ? session
                        : JSON.stringify(session),
                code: mfaCode,
                email: "", // May need to be provided by the session or extracted from context
                name: challengeName || "SMS_MFA",
            };

            const response = await this.accountClient.loginWithChallenge(
                apiBody
            );

            if (response.status === HttpStatus.OK) {
                const loginData = response.data as TokenResponse;
                return {
                    status: HttpStatus.OK,
                    data: {
                        AccessToken: loginData.AccessToken,
                        IdToken: loginData.IdToken,
                        RefreshToken: loginData.RefreshToken,
                        LastLogin: loginData.LastLogin,
                    } as TokenResponse,
                };
            }

            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    }
}
